import { Flex } from 'antd';
import { focusHaloBleed, stableScrollGutter } from './scrollGutter';
import { PinnedFormActions, pinnedActionsScroller } from './formCard.styled';
import styled, { css } from 'styled-components';
import { atPaneWidth } from './paneWidth';

export const CustomLegend = styled(Flex)`
  margin-top: 8px;
  flex-wrap: wrap;
  &.weekly-budget-legend {
    ${atPaneWidth(
      'min-width',
      400,
      css`
        gap: 50px !important;
      `,
    )}
  }
  .primary-bar-color {
    width: 14px;
    height: 6px;
    display: block;
    border-radius: 10px;
    background-color: var(--clr-general-primary);
    margin-right: 4px;
  }
  .secondary-bar {
    background-color: var(--clr-border-grey-100);
  }
  .legend-data {
    color: var(--clr-general-primary);
    padding-left: 4px;
  }
`;

export const PrimaryCard = styled.div<{ $isDetail?: boolean }>`
  border-radius: ${({ $isDetail }) => ($isDetail ? '10px' : '5px')};
  padding: ${({ $isDetail }) => ($isDetail ? 'var(--spacing-gutter)' : '10px')};
  overflow: visible;
  background-color: var(--clr-container-bg);
  width: 100%;
  transition: background 0.2s linear;
  &:hover {
    &.pointer {
      cursor: pointer;
      background-color: var(--clr-general-hover);
    }
  }

  .status-chip {
    color: var(--clr-title);
    padding: 4px 15px;
    font-size: 10px;
    line-height: 100%;
    border-radius: 30px;
    min-width: 80px;
    min-height: 23px;
    white-space: nowrap;
  }
  &.detailed-view {
    display: flex;
    flex-direction: column;
    /* fills the pane, keeping the bottom gutter the list screens leave below their last card */
    height: calc(100% - var(--spacing-gutter));
    margin-bottom: var(--spacing-gutter);
    .notes-data {
      p {
        overflow: visible;
        display: block;
      }
    }
    .detail-note-container {
      flex: 1;
      min-height: 0;
    }
    .notes-scrollable {
      flex: 0 1 auto;
      min-height: 0;
      overflow-y: auto;
      overscroll-behavior: contain;
      padding-right: 5px;
    }
    > :not(.detail-note-container),
    .detail-note-container > :not(.notes-scrollable) {
      flex-shrink: 0;
    }
  }
  .notes-data {
    overflow-wrap: anywhere;
    p {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
      max-width: 240px;
    }
  }
  &.transaction-active-card {
    border-color: var(--clr-general-primary) !important;
  }
  &.matter-card-wrapper {
    border: 1px solid var(--clr-border-grey-100);
    min-height: 62px;
    height: auto;
    flex-shrink: 0;
    display: flex;
    gap: 5px;
    cursor: pointer;
    background-color: var(--clr-container-bg);
    transition: all 0.3s ease;
    .icon {
      font-size: 18px;
      flex-shrink: 0;
      color: var(--clr-title);
      opacity: 0.2;
    }
    .icon[class*='text-clr-'] {
      opacity: 1;
    }
    &:hover {
      background: var(--clr-general-hover);
      border-color: var(--clr-general-border-hover);
    }
    &.transaction-active-card {
      background: var(--selected);
      border-color: var(--clr-general-primary);
    }
  }
  .total-transaction {
    padding: 10px;
    border-block: 1px solid var(--clr-border-grey-100);
    border-radius: 5px;
    align-items: center;
  }
  .task-status-chip {
    display: block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 80px;
    line-height: 14px;

    ${atPaneWidth(
      'min-width',
      400,
      css`
        max-width: none;
        overflow: visible;
        white-space: normal;
        text-overflow: unset;
      `,
    )}
  }
  .responsive-spacing {
    gap: 2px;
    ${atPaneWidth(
      'min-width',
      380,
      css`
        gap: 10px;
      `,
    )}
  }
`;

export const MatterListScrollArea = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  ${stableScrollGutter}
`;

const SUGGESTED_MATTER_HEADER_GAP = 10;
/** The expanded list's ceiling: three cards' worth, then it scrolls. */
const SUGGESTED_MATTER_LIST_HEIGHT = 185;
const SUGGESTED_MATTER_LIST_BOX_HEIGHT = SUGGESTED_MATTER_LIST_HEIGHT + SUGGESTED_MATTER_HEADER_GAP;
const PANEL_EXPAND_TRANSITION = '0.25s ease-in-out';
const PANEL_PADDING_TRANSITION = '0.12s ease-out';
const PANEL_PADDING_RETURN_DELAY = '0.13s';
export const SuggestedMatterPanel = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  padding: var(--spacing-gutter);
  border-radius: 5px;
  border: 1px solid var(--clr-general-border-hover);
  background-color: var(--clr-general-hover);
  user-select: none;
  transition: padding-bottom ${PANEL_PADDING_TRANSITION} ${PANEL_PADDING_RETURN_DELAY};
  .suggested-matter-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${SUGGESTED_MATTER_HEADER_GAP}px;
    cursor: pointer;
    margin: calc(var(--spacing-gutter) * -1) calc(var(--spacing-gutter) * -1) 0;
    padding: var(--spacing-gutter) var(--spacing-gutter) 0;
  }

  &:not(.expanded) .suggested-matter-header {
    margin-bottom: calc(var(--spacing-gutter) * -1);
    padding-bottom: var(--spacing-gutter);
  }

  .suggested-matter-chevron {
    display: flex;
    font-size: 16px;
    color: var(--clr-general-description);
    transition: transform ${PANEL_EXPAND_TRANSITION};
  }

  .suggested-matter-list {
    display: flex;
    flex-direction: column;
    max-height: 0;
    overflow: hidden;
    visibility: hidden;
    transition:
      max-height ${PANEL_EXPAND_TRANSITION},
      visibility ${PANEL_EXPAND_TRANSITION};
  }

  ${MatterListScrollArea} {
    margin-top: ${SUGGESTED_MATTER_HEADER_GAP}px;
    padding-bottom: var(--spacing-gutter);
  }

  &.expanded {
    padding-bottom: 0;
    /* opening, the room is needed straight away */
    transition-delay: 0s;

    .suggested-matter-list {
      max-height: ${SUGGESTED_MATTER_LIST_BOX_HEIGHT}px;
      visibility: visible;
    }

    .suggested-matter-chevron {
      transform: rotate(180deg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    &,
    .suggested-matter-list,
    .suggested-matter-chevron {
      transition: none;
    }
  }
`;

export const CollapsibleChartPanelWrapper = styled.div`
  width: 100%;
  overflow: hidden;
  border: 1px solid var(--clr-border-grey-100);
  border-radius: 5px;
  background-color: var(--clr-container-bg);
  user-select: none;

  .chart-panel-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border-radius: 5px;
    font-size: 14px;
    color: var(--clr-general-primary);
    background-color: var(--clr-general-hover);
    transition: background-color 0.2s linear;
    ${atPaneWidth(
      'min-width',
      381,
      css`
        width: 36px;
        height: 36px;
        font-size: 16px;
      `,
    )}
  }

  .chart-panel-heading {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
  }

  .chart-panel-subtitle {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    column-gap: 5px;
  }

  .chart-panel-subtitle-separator {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    flex-shrink: 0;
    background-color: rgba(var(--clr-title-rgb), 0.2);
  }

  .chart-panel-chevron {
    display: flex;
    flex-shrink: 0;
    font-size: 18px;
    color: var(--clr-general-description);
    transition: transform ${PANEL_EXPAND_TRANSITION};
    ${atPaneWidth(
      'min-width',
      381,
      css`
        font-size: 22px;
      `,
    )}
  }

  /* the chart stays mounted while collapsed so opening and closing it is one height animation */
  .chart-panel-body {
    display: grid;
    grid-template-rows: 0fr;
    visibility: hidden;
    transition:
      grid-template-rows ${PANEL_EXPAND_TRANSITION},
      visibility ${PANEL_EXPAND_TRANSITION};
  }

  .chart-panel-content {
    min-height: 0;
    overflow: hidden;
  }

  .chart-panel-chart {
    padding: var(--spacing-gutter);
    border-top: 1px solid var(--clr-border-grey-100);
  }

  &.expanded {
    .chart-panel-body {
      grid-template-rows: 1fr;
      visibility: visible;
    }
    .chart-panel-chevron {
      transform: rotate(180deg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .chart-panel-icon,
    .chart-panel-body,
    .chart-panel-chevron {
      transition: none;
    }
  }
`;

export const ChartPanelHeader = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px;
  border: 0;
  background-color: transparent;
  font: inherit;
  color: inherit;
  text-align: start;
  cursor: pointer;
  transition: background-color 0.2s linear;
  &:hover {
    background-color: var(--clr-general-hover);
    .chart-panel-icon {
      background-color: var(--clr-container-bg);
    }
  }
  /* The panel clips to its rounded border, so a ring outside the header would never show. */
  &:focus-visible {
    outline: 2px solid var(--clr-general-primary);
    outline-offset: -2px;
    border-radius: 4px;
  }
  ${atPaneWidth(
    'min-width',
    381,
    css`
      gap: 10px;
    `,
  )}
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const TransactionTypeCategoryHeaderButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: 0;
  border: 0;
  background-color: transparent;
  font: inherit;
  color: inherit;
  text-align: start;
  cursor: pointer;

  .category-header-chevron {
    display: flex;
    flex-shrink: 0;
    font-size: 16px;
    color: var(--clr-general-primary);
    transition: transform ${PANEL_EXPAND_TRANSITION};
  }

  &[aria-expanded='false'] .category-header-chevron {
    transform: rotate(-90deg);
  }

  @media (prefers-reduced-motion: reduce) {
    .category-header-chevron {
      transition: none;
    }
  }
`;

export const EmailFileWrapper = styled(Flex)`
  border: 1px solid var(--clr-border-grey-100);
  padding: 5px 10px;
  border-radius: 5px;
  .email-file-name {
    .icon {
      font-size: 15px;
      color: var(--clr-icon-color);
      flex-shrink: 0;
    }
    p {
      font-size: 12px;
      color: var(--clr-title);
      line-height: 16px;
      width: 100%;
      word-break: break-all;
    }
  }
  .email-more-info {
    padding-left: 20px;
    p {
      font-size: 11px;
      color: var(--clr-general-description);
    }
  }
`;

export const AddSubjectWrapper = styled(Flex)`
  height: 100%;
  overflow-y: auto;
  ${focusHaloBleed}
  ${pinnedActionsScroller}
  ${PinnedFormActions} {
    background-color: var(--clr-body-bg);
  }
  .change-value-btn {
    right: -57px;
  }
  .imp-note-wrapper {
    padding: 15px;
    border: 1px solid var(--clr-border-grey-100);
    backdrop-filter: blur(40px);
    background: var(--clr-container-bg);
    border-radius: 5px;
  }
`;

export const FavouriteCta = styled.button`
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  backdrop-filter: blur(40px);
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: var(--clr-container-bg);
  border: 1px solid var(--clr-border-grey-100);
  &:hover {
    background: var(--clr-general-hover);
    border-color: var(--clr-general-border-hover);
    .icon {
      color: var(--clr-general-primary);
    }
  }
  &.active {
    background: var(--selected);
    border-color: var(--clr-general-primary);
    .icon {
      color: var(--clr-general-primary);
    }
    &:hover {
      background: var(--selected-hover);
    }
  }
  &:not(.active):active {
    background: var(--selected);
    border-color: var(--clr-general-primary);
    .icon {
      color: var(--clr-general-primary);
    }
  }
  .icon {
    font-size: 16px;
    color: var(--clr-general-description);
  }
`;

export const DateGroupHeader = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-width: 0;
  padding: 0;
  border: 0;
  border-radius: 3px;
  background: transparent;
  font: inherit;
  text-align: left;
  cursor: pointer;
  &:focus-visible {
    /* 1px offset keeps the 3px reach inside the list's halo padding */
    outline: 2px solid var(--clr-general-primary);
    outline-offset: 1px;
  }
  .date-group-chevron {
    display: flex;
    flex-shrink: 0;
    font-size: 16px;
    color: var(--clr-general-primary);
    transition: transform 0.2s ease-in-out;
  }
  &[aria-expanded='false'] .date-group-chevron {
    transform: rotate(-90deg);
  }
  .date-group-title,
  .date-group-count,
  .date-group-total {
    /* the Typography default of width: inherit would stretch each span to the full row */
    width: auto;
  }
  .date-group-title,
  .date-group-total {
    flex-shrink: 0;
    white-space: nowrap;
  }
  .date-group-count {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .date-group-total {
    margin-left: auto;
    padding-left: 10px;
  }
  @media (prefers-reduced-motion: reduce) {
    .date-group-chevron {
      transition: none;
    }
  }
`;

export const PriorityChip = styled.div`
  height: 23px;
  background-color: var(--clr-body-bg);
  border: 1px solid var(--clr-border-grey-100);
  border-radius: 30px;
  padding: 4px 12px;
  display: flex;
  gap: 4px;
  justify-content: center;
  align-items: center;
  font-size: 10px;
  color: var(--clr-title);
  line-height: 15px;
  svg {
    width: 12px;
    height: 12px;
  }
`;

export const MessageContainer = styled(Flex)`
  padding: 10px;
  border-radius: 5px;
  background: var(--selected);
  .icon {
    flex-shrink: 0;
  }
  .ant-btn {
    padding: 0;
    width: 20px;
    height: 20px;
    .icon {
      font-size: 20px;
      color: var(--clr-subtitle);
    }
  }
`;

export const TransactionActionWrapper = styled(Flex)`
  border-top: 1px solid var(--clr-border-grey-100);
  padding-block: var(--spacing-gutter);
  flex-shrink: 0;
  margin-inline: calc(var(--spacing-gutter) * -1);
  padding-inline: var(--spacing-gutter);
  background-color: var(--clr-container-bg);
  margin-bottom: calc(var(--spacing-gutter) * -1);

  .action-btn-group {
    .action-btn {
      flex: 1;
      min-width: 0;
      height: auto;
      gap: 4px;
      padding: 10px 6px;
      text-align: center;
      .icon {
        font-size: 20px;
        line-height: 1;
      }
    }
  }
`;

// Footer rendered outside the white card (via the layout footer slot):
export const MatterRequestFooter = styled(Flex)`
  .action-divider {
    &.ant-divider-with-text {
      margin: 0;
      color: var(--clr-general-description);
      font-size: 11px;
      font-weight: 400;
      .ant-divider-inner-text {
        padding-inline: 10px;
      }
    }
  }

  .matter-request-btn {
    &.ant-btn {
      background-color: var(--clr-general-secondary);
      border-color: var(--clr-general-secondary);
      color: var(--clr-white);
      .icon {
        font-size: 16px;
        color: var(--clr-white);
      }
      &:not(:disabled):not(.ant-btn-disabled):hover {
        background-color: var(--clr-general-secondary);
        border-color: var(--clr-general-secondary);
        color: var(--clr-white);
        opacity: 0.9;
      }
    }
  }
`;
